import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAllTemplates, deleteTemplate } from "../../../data/requests/template-requests";
import { AdminLayout } from "../shared/layout";
import { useNavigate } from "react-router-dom";
import { PlusCircle, Eye, Pencil, Trash2, Search } from "lucide-react";
import { DataTable } from "@/components/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";

export function TemplateListScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  
  const { data, isLoading } = useQuery({
    queryKey: ["admin-templates", { search, category }],
    queryFn: () => getAllTemplates({ search, category }).then(res => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(["admin-templates"]);
      toast({
        title: "Success",
        description: "Template deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete template",
        variant: "destructive",
      });
    },
  });

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      deleteMutation.mutate(id);
    }
  };

  const columns = [
    { 
      key: "thumbnailUrl", 
      label: "Preview",
      render: (row) => (
        <img 
          src={row.thumbnailUrl || row.imageUrl} 
          alt={row.name}
          className="w-20 h-20 object-cover rounded-lg shadow-sm border border-gray-200"
        />
      )
    },
    { key: "name", label: "Name" },
    { 
      key: "category", 
      label: "Category",
      render: (row) => row.category || "—"
    },
    { 
      key: "webhookUrl", 
      label: "Webhook",
      render: (row) => (
        <Badge variant={row.webhookUrl ? "default" : "secondary"}>
          {row.webhookUrl ? "Configured" : "None"}
        </Badge>
      )
    },
    { 
      key: "isPublished", 
      label: "Status", 
      render: (row) => (
        <Badge variant={row.isPublished ? "success" : "secondary"}>
          {row.isPublished ? "Published" : "Draft"}
        </Badge>
      )
    },
    { 
      key: "actions", 
      label: "Actions", 
      render: (row) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/templates/${row.id}`)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/templates/${row.id}/edit`)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.id)}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Templates</h1>
          <Button onClick={() => navigate("/admin/templates/new")}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Template
          </Button>
        </div>

        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <DataTable
          data={data?.templates || []}
          columns={columns}
          isLoading={isLoading}
        />
        
        <div className="text-sm text-gray-500 mt-4">
          <p>💡 <strong>Kullanıcılara template atamak için:</strong> Users sayfasından kullanıcıyı düzenleyin.</p>
        </div>
      </div>
    </AdminLayout>
  );
}

